import type { DineKotPrint } from 'api/types';

/**
 * DO-308 - the kitchen ticket on paper. An 80 mm layout opened in its own window
 * and sent to the browser's print dialog, so the kitchen printer the station's
 * screen can reach gets it. Big type: a ticket is read from a metre away in a
 * hot kitchen. The payload is the ticket as sent; nothing is recomputed.
 */
const esc = (s: string | undefined | null) => (s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

export function kotPrintHtml(k: DineKotPrint): string {
  const when = new Date(k.sentAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  // As the server's KotTicketComposer.Where / NcDetail print it: a requisition is an NC (non-chargeable) KOT.
  const isNc = k.orderChannel === 'REQUISITION';
  const ncDetail = isNc
    ? [k.requisitionNumber, k.requisitionFor?.toUpperCase(), k.requisitionPurpose].filter(Boolean).map(esc).join(' - ')
    : '';
  const where = isNc
    ? 'NC KOT'
    : k.isRoomService
      ? `ROOM ${esc(k.roomNumber ?? '')}${k.guestPropertyName ? ` · ${esc(k.guestPropertyName)}` : ''}`
      : k.tableNumber ? `TABLE ${esc(k.tableNumber)}` : 'TAKEAWAY';
  // REMAKE: a returned dish made again or replaced (return dish); inverted like CANCEL, as KotTicketComposer prints it.
  const kind = k.kotType === 'ADDITION' ? 'ADDITION' : k.kotType === 'CANCEL' ? 'CANCEL' : k.kotType === 'REMAKE' ? 'REMAKE' : 'NEW';
  const lines = k.lines.map((l) => `
    <tr>
      <td class="q">${l.quantity}</td>
      <td class="n">${esc(l.itemName)}${l.variantName ? `<div class="v">${esc(l.variantName)}</div>` : ''}${l.instructions ? `<div class="i">${esc(l.instructions)}</div>` : ''}</td>
    </tr>`).join('');

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(k.kotNumber)}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  body { font-family: "Courier New", monospace; font-size: 14px; width: 72mm; margin: 0; color: #000; }
  .hd { text-align: center; }
  .st { font-size: 15px; font-weight: 700; letter-spacing: 1px; }
  .kind { display: inline-block; margin: 4px 0; padding: 2px 10px; border: 2px solid #000; font-weight: 700; font-size: 16px; }
  .kind.cancel { background: #000; color: #fff; }
  .fb { margin: 2px 0 4px; padding: 2px 6px; background: #000; color: #fff; font-weight: 700; font-size: 14px; }
  .where { font-size: 22px; font-weight: 700; margin: 4px 0; }
  .nc { font-size: 13px; font-weight: 700; margin-bottom: 4px; }
  .meta { font-size: 12px; }
  .rule { border-top: 2px dashed #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { vertical-align: top; padding: 4px 0; }
  td.q { width: 2.4em; font-size: 20px; font-weight: 700; }
  td.n { font-size: 17px; font-weight: 700; }
  .v { font-size: 13px; font-weight: 400; }
  .i { font-size: 13px; font-weight: 700; font-style: italic; margin-top: 2px; }
  .ft { text-align: center; font-size: 12px; margin-top: 6px; }
</style></head><body>
  <div class="hd">
    <div class="st">${esc(k.stationName ?? 'KITCHEN')}</div>
    ${k.fallbackFromStationName ? `<div class="fb">FALLBACK FROM ${esc(k.fallbackFromStationName.toUpperCase())}</div>` : ''}
    <div class="kind ${kind === 'CANCEL' || kind === 'REMAKE' ? 'cancel' : ''}">${kind}${k.roundNumber > 1 && kind !== 'CANCEL' ? ` · ROUND ${k.roundNumber}` : ''}</div>
    <div class="where">${where}</div>
    ${ncDetail ? `<div class="nc">${ncDetail}</div>` : ''}
    ${isNc && k.requisitionApprovedBy ? `<div class="meta">APPROVED BY ${esc(k.requisitionApprovedBy)}</div>` : ''}
    <div class="meta">${k.outletCode ? `${esc(k.outletCode)} · ` : ''}${esc(k.kotNumber)} · ${esc(when)}${k.waiterName ? ` · ${esc(k.waiterName)}` : ''} · ${k.covers} cover${k.covers === 1 ? '' : 's'}</div>
  </div>
  <div class="rule"></div>
  <table><tbody>${lines}</tbody></table>
  <div class="rule"></div>
  <div class="ft">${kind === 'CANCEL' ? 'DO NOT PREPARE — cancelled' : `${k.lines.reduce((s, l) => s + l.quantity, 0)} item(s)`}${k.printerName ? ` · ${esc(k.printerName)}` : ''}</div>
</body></html>`;
}

/** Opens the ticket in a print window. Returns false when the browser blocked the popup. */
export function printKot(k: DineKotPrint): boolean {
  const w = window.open('', '_blank', 'width=420,height=640');
  if (!w) return false;
  w.document.open();
  w.document.write(kotPrintHtml(k));
  w.document.close();
  setTimeout(() => { w.print(); }, 250);
  return true;
}
